
import { useState, useEffect, useCallback, useRef } from 'react';
import { DevelopmentStep } from '@/types/develop';

export const useDevelopTimer = (steps: DevelopmentStep[], autoStartNextStep: boolean = false) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [agitationDue, setAgitationDue] = useState(false);
  const [stepComplete, setStepComplete] = useState(false);
  const [allComplete, setAllComplete] = useState(false);
  const [nextAgitationTime, setNextAgitationTime] = useState<number | null>(null);
  const [initialAgitationComplete, setInitialAgitationComplete] = useState(false);
  
  const timerRef = useRef<number | null>(null);
  const lastTickTimeRef = useRef<number | null>(null);
  const agitationTimerRef = useRef<number | null>(null);
  const lastAgitationTimeRef = useRef<number>(0);
  const lastAgitationCheckRef = useRef<number>(0);
  const initialAgitationHandledRef = useRef<boolean>(false);
  
  const currentStep = steps[currentStepIndex];
  
  // Determine if current step is developer or fixer (needs special agitation)
  const isSpecialAgitationStep = currentStep?.name?.toLowerCase().includes('developer') || 
                                 currentStep?.name?.toLowerCase().includes('fixer');
  
  // Reset initialAgitationComplete when step changes
  useEffect(() => {
    setInitialAgitationComplete(false);
    initialAgitationHandledRef.current = false; // Reset when step changes
  }, [currentStepIndex]);
  
  // Calculate next agitation time
  useEffect(() => {
    if (!currentStep) return;
    
    if (isSpecialAgitationStep && !initialAgitationComplete && elapsedTime < 30) {
      // During first 30 seconds of developer/fixer, agitation is continuous
      setNextAgitationTime(null);
    } else if (currentStep.agitationType === 'intermittent' && currentStep.agitationInterval) {
      // Calculate next agitation time after initial 30s
      if (isSpecialAgitationStep && !initialAgitationComplete && elapsedTime >= 30) {
        // First regular agitation after initial 30s period
        setInitialAgitationComplete(true);
        // Set the next agitation time to 30s + interval, skipping the first marker at exactly 30s
        setNextAgitationTime(30 + currentStep.agitationInterval);
      } else if (initialAgitationComplete || !isSpecialAgitationStep) {
        // Regular interval-based agitation
        const timeUntilNextAgitation = currentStep.agitationInterval - 
          ((elapsedTime - (isSpecialAgitationStep ? 30 : 0)) % currentStep.agitationInterval);
        setNextAgitationTime(elapsedTime + timeUntilNextAgitation);
      }
    } else {
      setNextAgitationTime(null);
    }
  }, [elapsedTime, currentStep, isSpecialAgitationStep, initialAgitationComplete]);
  
  // Function to start the timer
  const startTimer = useCallback(() => {
    if (!isRunning) {
      lastTickTimeRef.current = Date.now();
      setIsRunning(true);
    }
  }, [isRunning]);
  
  // Function to pause the timer
  const pauseTimer = useCallback(() => {
    setIsRunning(false);
  }, []);
  
  // Function to reset the current step
  const resetStep = useCallback(() => {
    setElapsedTime(0);
    setAgitationDue(false);
    setStepComplete(false);
    setInitialAgitationComplete(false);
    initialAgitationHandledRef.current = false;
    lastAgitationTimeRef.current = 0;
    lastAgitationCheckRef.current = 0;
  }, []);
  
  // Function to skip the current step and go to the next
  const skipStep = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      // Mark current step as complete and move to next
      setStepComplete(true);
      nextStep();
    }
  }, [currentStepIndex, steps.length]);
  
  // Function to go to the next step
  const nextStep = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(prevIndex => prevIndex + 1);
      resetStep();
      // Start the timer automatically if autoStartNextStep is enabled
      if (autoStartNextStep) {
        setIsRunning(true);
        lastTickTimeRef.current = Date.now();
      } else {
        setIsRunning(false);
      }
    } else {
      setAllComplete(true);
      pauseTimer();
    }
  }, [currentStepIndex, steps.length, resetStep, pauseTimer, autoStartNextStep]);
  
  // Function to go back to the previous step
  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prevIndex => prevIndex - 1);
      resetStep();
      // We don't auto-start when going back either
      setIsRunning(false);
    }
  }, [currentStepIndex, resetStep]);
  
  // Function to reset the entire timer
  const resetTimer = useCallback(() => {
    setCurrentStepIndex(0);
    resetStep();
    setAllComplete(false);
    setIsRunning(false); // Ensure timer doesn't start on reset
  }, [resetStep]);
  
  // Get total elapsed time for the current session
  const getTotalElapsedTime = useCallback(() => {
    // Sum up times from completed steps
    const completedStepsTime = steps
      .slice(0, currentStepIndex)
      .reduce((total, step) => total + step.time, 0);
    
    // Add current step's elapsed time
    return completedStepsTime + (stepComplete ? currentStep?.time || 0 : elapsedTime);
  }, [steps, currentStepIndex, elapsedTime, stepComplete, currentStep]);
  
  // Function to trigger vibration if available
  const triggerVibration = useCallback(() => {
    if (window.navigator && window.navigator.vibrate) {
      window.navigator.vibrate([200, 100, 200]); // Vibrate pattern: 200ms on, 100ms off, 200ms on
    }
  }, []);

  // Update timer state on each tick
  useEffect(() => {
    if (isRunning && !stepComplete) {
      const tick = () => {
        const now = Date.now();
        const delta = lastTickTimeRef.current ? (now - lastTickTimeRef.current) / 1000 : 0;
        lastTickTimeRef.current = now;
        
        setElapsedTime(prevTime => {
          const newTime = prevTime + delta;
          
          if (newTime >= currentStep?.time) {
            // Step is complete
            setStepComplete(true);
            setAgitationDue(false);
            return currentStep.time;
          }
          
          return newTime;
        });
      };
      
      timerRef.current = window.setInterval(tick, 100);
      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      };
    }
  }, [isRunning, stepComplete, currentStep]);
  
  // Handle initial 30 second agitation for developer and fixer steps
  useEffect(() => {
    if (!isRunning || !currentStep || !isSpecialAgitationStep) {
      return;
    }
    
    // For first 30 seconds of developer and fixer, show continuous agitation
    if (elapsedTime < 30) {
      if (!agitationDue) {
        console.log("INITIAL AGITATION STARTED for special step");
        setAgitationDue(true);
        triggerVibration();
      }
    } else if (elapsedTime >= 30 && !initialAgitationComplete && !initialAgitationHandledRef.current) {
      // Once we hit 30 seconds, mark initial agitation complete
      console.log("INITIAL 30s AGITATION COMPLETE for special step");
      initialAgitationHandledRef.current = true; // Mark that we've handled the 30s transition
      setInitialAgitationComplete(true);
      setAgitationDue(false); // Clear the agitation notification
      
      // Do not immediately trigger the next agitation at exactly 30s
      lastAgitationTimeRef.current = 30;
    }
  }, [elapsedTime, isRunning, currentStep, isSpecialAgitationStep, initialAgitationComplete, agitationDue, triggerVibration]);
  
  // Handle regular interval-based agitation after initial phase
  useEffect(() => {
    // Only run for interval agitation after initial phase is complete (or for non-special steps)
    if (!isRunning || 
        !currentStep || 
        currentStep.agitationType !== 'intermittent' || 
        !currentStep.agitationInterval ||
        (isSpecialAgitationStep && !initialAgitationComplete && elapsedTime < 30)) {
      return;
    }

    // Clear any existing interval to prevent duplicates
    if (agitationTimerRef.current) {
      clearInterval(agitationTimerRef.current);
    }
    
    // Function to check if agitation is needed
    const checkAgitation = () => {
      // Skip if we don't have proper configuration
      if (!currentStep.agitationInterval) return;
      
      // For special steps, we need to offset by 30 seconds
      const adjustedTime = isSpecialAgitationStep ? (elapsedTime - 30) : elapsedTime;
      const adjustedLastCheck = isSpecialAgitationStep ? (lastAgitationCheckRef.current - 30) : lastAgitationCheckRef.current;
      
      if (adjustedTime < 0) return; // Still in initial agitation phase
      
      // Calculate which agitation cycle we're in
      const currentAgitationCycle = Math.floor(adjustedTime / currentStep.agitationInterval);
      const previousAgitationCycle = Math.floor(adjustedLastCheck / currentStep.agitationInterval);
      
      // Update the last check time
      lastAgitationCheckRef.current = elapsedTime;
      
      // Skip the first agitation notification right at the 30-second mark for special steps
      if (isSpecialAgitationStep && 
          initialAgitationHandledRef.current && 
          Math.abs(elapsedTime - 30) < 0.5 && 
          currentAgitationCycle === 0) {
        console.log("Skipping first agitation marker at 30s transition");
        return;
      }
      
      // If we've entered a new agitation cycle, trigger notification
      if (currentAgitationCycle > previousAgitationCycle) {
        console.log("REGULAR AGITATION DUE! Current cycle:", currentAgitationCycle, "Previous cycle:", previousAgitationCycle);
        // Trigger vibration
        triggerVibration();
        setAgitationDue(true);
        
        // Record the time this agitation was triggered
        lastAgitationTimeRef.current = elapsedTime;
        
        // Auto-clear agitation due after the agitation duration if specified
        if (currentStep.agitationDuration) {
          setTimeout(() => {
            console.log("Auto-clearing agitation due after duration");
            setAgitationDue(false);
          }, currentStep.agitationDuration * 1000);
        }
      }
      
      // If user has manually cleared the agitation flag, update lastAgitationTimeRef
      if (!agitationDue && lastAgitationTimeRef.current > 0 && 
          elapsedTime - lastAgitationTimeRef.current < currentStep.agitationInterval) {
        lastAgitationTimeRef.current = elapsedTime;
      }
    };
    
    // Check immediately when the timer starts or when transitioning from initial to regular agitation
    checkAgitation();
    
    // Then set up an interval to check frequently (every 200ms for more precise detection)
    agitationTimerRef.current = window.setInterval(checkAgitation, 200);
    
    // Cleanup function
    return () => {
      if (agitationTimerRef.current) {
        clearInterval(agitationTimerRef.current);
        agitationTimerRef.current = null;
      }
    };
  }, [isRunning, currentStep, elapsedTime, agitationDue, triggerVibration, isSpecialAgitationStep, initialAgitationComplete]);
  
  // Effect to handle auto-advance to next step
  useEffect(() => {
    if (stepComplete && autoStartNextStep && currentStepIndex < steps.length - 1) {
      // Use a small timeout to allow the UI to update and show completion first
      const autoAdvanceTimeout = setTimeout(() => {
        nextStep();
      }, 1000); // Wait 1 second before auto-advancing
      
      return () => {
        clearTimeout(autoAdvanceTimeout);
      };
    }
  }, [stepComplete, autoStartNextStep, currentStepIndex, steps.length, nextStep]);
  
  // Calculate agitation markers for the progress bar
  const getAgitationTimes = useCallback(() => {
    if (!currentStep || 
        currentStep.agitationType !== 'intermittent' || 
        !currentStep.agitationInterval || 
        elapsedTime >= currentStep.time) {
      return [];
    }
    
    const times = [];
    let nextTime;
    
    if (isSpecialAgitationStep) {
      // For developer/fixer steps:
      // First 30 seconds are continuous agitation
      // After that, regular interval markers starting at 30s + interval (not at exactly 30s)
      if (elapsedTime < 30) {
        // Show the end of continuous phase marker at exactly 30s
        times.push(30);
        
        // Add regular intervals after 30s, starting with 30 + interval (not at 30s)
        nextTime = 30 + currentStep.agitationInterval;
      } else {
        // After 30s, calculate next regular agitation time
        // Start from 30s + first interval, not just 30s
        const firstIntervalAfter30 = 30 + currentStep.agitationInterval;
        
        nextTime = 30 + (Math.ceil((elapsedTime - 30) / currentStep.agitationInterval) * currentStep.agitationInterval);
        
        // If we're just past 30s and haven't had the first regular agitation yet,
        // make sure we show the correct first interval marker
        if (elapsedTime < firstIntervalAfter30) {
          nextTime = firstIntervalAfter30;
        }
      }
    } else {
      // Normal steps - regular intervals from the start
      nextTime = Math.ceil(elapsedTime / currentStep.agitationInterval) * currentStep.agitationInterval;
    }
    
    // Add all future agitation markers
    while (nextTime < currentStep.time) {
      if (!times.includes(nextTime)) {
        times.push(nextTime);
      }
      nextTime += currentStep.agitationInterval;
    }
    
    return times;
  }, [currentStep, elapsedTime, isSpecialAgitationStep]);
  
  // New function to get the current agitation instruction text
  const getAgitationInstructionText = useCallback(() => {
    if (!currentStep) return "";
    
    if (isSpecialAgitationStep && elapsedTime < 30) {
      return "Continuous agitation for first 30 seconds";
    } else if (currentStep.agitationType === 'continuous') {
      return "Continuous agitation";
    } else if (currentStep.agitationType === 'intermittent' && currentStep.agitationInterval) {
      return `Every ${currentStep.agitationInterval} seconds for ${currentStep.agitationDuration} seconds`;
    } else {
      return "No agitation needed";
    }
  }, [currentStep, isSpecialAgitationStep, elapsedTime]);
  
  return {
    currentStep,
    currentStepIndex,
    isRunning,
    elapsedTime,
    agitationDue,
    stepComplete,
    allComplete,
    startTimer,
    pauseTimer,
    resetStep,
    nextStep,
    prevStep,
    resetTimer,
    getTotalElapsedTime,
    skipStep,
    steps,
    nextAgitationTime,
    getAgitationTimes,
    isInitialAgitationPhase: isSpecialAgitationStep && elapsedTime < 30,
    getAgitationInstructionText
  };
};
